// ============================================================================
// FSA TEMPLATE MASTER SCREEN
// ============================================================================
// Read-only Template Browser for fsa.tbl_fs_template via prima-api
// /api/v1/FsaTemplates endpoint.
//
// Stage 1 ships VIEW only — no Add / Edit / Delete / Clone. The action menu
// shows only "View" when the role has the `view` permission.
//
// Per prima-web/CLAUDE.md Module Boundaries: FSA screens live under
// src/components/modules/fsa/.

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  ColumnSelector,
  type ColumnDefinition,
} from 'components/ui';
import { useFsaTemplates } from 'hooks/api/fsa/useFsaTemplates';
import type {
  FsTemplate,
  FsTemplateSearchRequest,
  EntityType,
} from 'api/types/fsa/template.types';
import FsaTemplateViewModal from 'components/modals/modules/fsa/FsaTemplateViewModal';
import FsaNarrativeViewModal from 'components/modals/modules/fsa/FsaNarrativeViewModal';

type ViewMode = 'list' | 'grid';

const ENTITY_TYPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'COMPANY', label: 'Company' },
  { value: 'LLP', label: 'LLP' },
  { value: 'PARTNERSHIP', label: 'Partnership' },
  { value: 'SOLE_PROPRIETOR', label: 'Sole Proprietor' },
  { value: 'TRUST', label: 'Trust' },
];

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'ACTIVE', label: 'Active only' },
  { value: 'INACTIVE', label: 'Inactive only' },
];

const ORIGIN_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'system', label: 'System (seeded)' },
  { value: 'firm', label: 'Firm (custom)' },
];

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
};

interface FsaTemplateMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /** Permitted actions for the current role. Stage 1 only honours `view`. */
  allowedActions?: string[];
}

const columnDefinitions: ColumnDefinition[] = [
  { id: 'kind',            label: 'Kind',            field: 'kind',            required: true,  defaultVisible: true,  sortable: true },
  { id: 'code',            label: 'Code',            field: 'code',            required: true,  defaultVisible: true,  sortable: true },
  { id: 'name',            label: 'Name',            field: 'name',            required: true,  defaultVisible: true,  sortable: true },
  { id: 'version',         label: 'Version',         field: 'version',         required: false, defaultVisible: true,  sortable: true },
  { id: 'entityType',      label: 'Entity Type',     field: 'entityType',      required: false, defaultVisible: true,  sortable: true },
  { id: 'jurisdiction',    label: 'Jurisdiction',    field: 'jurisdictionCode', required: false, defaultVisible: true, sortable: true },
  { id: 'statutoryBasis',  label: 'Statutory Basis', field: 'statutoryBasis',  required: false, defaultVisible: true,  sortable: true },
  { id: 'applicableFrom',  label: 'Applicable From', field: 'applicableFrom',  required: false, defaultVisible: true,  sortable: true },
  { id: 'applicableTo',    label: 'Applicable To',   field: 'applicableTo',    required: false, defaultVisible: false, sortable: true },
  { id: 'currency',        label: 'Currency',        field: 'defaultCurrencyCode', required: false, defaultVisible: false, sortable: false },
  { id: 'rounding',        label: 'Rounding',        field: 'defaultRoundingFactorLabel', required: false, defaultVisible: false, sortable: false },
  { id: 'origin',          label: 'Origin',          field: 'isSystem',        required: false, defaultVisible: true,  sortable: false },
  { id: 'status',          label: 'Status',          field: 'statusName',      required: false, defaultVisible: true,  sortable: true },
  { id: 'createdAt',       label: 'Created',         field: 'createdAt',       required: false, defaultVisible: false, sortable: true },
  { id: 'updatedAt',       label: 'Updated',         field: 'updatedAt',       required: false, defaultVisible: false, sortable: true },
];

const COLUMN_PREF_KEY = 'fsa-template-master-columns';

const FsaTemplateMasterScreen: React.FC<FsaTemplateMasterScreenProps> = ({
  moduleIcon,
  tabId,
  moduleName,
  allowedActions = [],
}) => {
  // Suppress unused destructure warnings for props reserved for future use.
  void tabId;
  void moduleName;

  // Column visibility persistence
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id)
  );

  useEffect(() => {
    const saved = localStorage.getItem(COLUMN_PREF_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setVisibleColumns(parsed);
      } catch { /* ignore */ }
    }
  }, []);

  const handleApplyColumns = useCallback((cols: string[]) => {
    setVisibleColumns(cols);
    localStorage.setItem(COLUMN_PREF_KEY, JSON.stringify(cols));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaults = columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id);
    setVisibleColumns(defaults);
    localStorage.removeItem(COLUMN_PREF_KEY);
  }, []);

  // Server-side query state. includeNarratives — ADR-0005: the browser
  // lists installed narrative components in the same grid (Kind badge);
  // template-selection callers elsewhere never set this flag.
  const [queryParams, setQueryParams] = useState<FsTemplateSearchRequest>({
    page: 1,
    pageSize: 25,
    includeInactive: true,
    includeNarratives: true,
  });
  const { data, pagination, loading, refetch } = useFsaTemplates(queryParams);

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // Filter inputs (drafts — pushed to queryParams on Apply)
  const [codeFilter, setCodeFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>('');
  const [jurisdictionFilter, setJurisdictionFilter] = useState('');
  const [statutoryBasisFilter, setStatutoryBasisFilter] = useState('');
  const [originFilter, setOriginFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams((prev) => ({
        ...prev,
        searchTerm: searchQuery || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Pagination
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams((prev) => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams((prev) => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState<FsTemplate | null>(null);

  // ADR-0005 — narrative component viewer (Kind = NARRATIVE rows).
  const [narrativeView, setNarrativeView] = useState<{ code: string; version: number } | null>(null);

  const handleView = useCallback((record: FsTemplate) => {
    if (record.kind === 'NARRATIVE') {
      setNarrativeView({ code: record.code, version: record.version });
      return;
    }
    setViewRecord(record);
    setViewModalOpen(true);
  }, []);

  // Column definitions
  const tableColumns: SyutGridColumn<FsTemplate>[] = useMemo(
    () => [
      {
        id: 'kind',
        header: 'Kind',
        accessorKey: 'kind',
        size: 110,
        enableSorting: true,
        cell: ({ row }) =>
          row.original.kind === 'NARRATIVE' ? (
            <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 999, background: '#ede9fe', color: '#5b21b6', fontWeight: 600 }}
              title="Independently versioned narrative component (Auditor's / Director's Report, CARO, Policy)">
              Narrative
            </span>
          ) : (
            <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 999, background: '#dbeafe', color: '#1e3a8a', fontWeight: 600 }}
              title="Financial-statement template (full report/schedule/class tree)">
              Template
            </span>
          ),
      },
      { id: 'code', header: 'Code', accessorKey: 'code', size: 160, enableSorting: true },
      { id: 'name', header: 'Name', accessorKey: 'name', enableSorting: true },
      {
        id: 'version',
        header: 'Version',
        accessorKey: 'version',
        size: 90,
        enableSorting: true,
        cell: ({ row }) => (
          <span style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>v{row.original.version}</span>
        ),
      },
      {
        id: 'entityType',
        header: 'Entity Type',
        accessorKey: 'entityType',
        size: 130,
        enableSorting: true,
        cell: ({ row }) =>
          row.original.entityType ? (
            <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 4, background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}>
              {row.original.entityType}
            </span>
          ) : '—',
      },
      {
        id: 'jurisdiction',
        header: 'Jurisdiction',
        accessorKey: 'jurisdictionCode',
        size: 110,
        enableSorting: true,
        cell: ({ row }) => row.original.jurisdictionCode || '—',
      },
      {
        id: 'statutoryBasis',
        header: 'Statutory Basis',
        accessorKey: 'statutoryBasis',
        size: 200,
        enableSorting: true,
        cell: ({ row }) => row.original.statutoryBasis || '—',
      },
      {
        id: 'applicableFrom',
        header: 'Applicable From',
        accessorKey: 'applicableFrom',
        size: 140,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.applicableFrom),
      },
      {
        id: 'applicableTo',
        header: 'Applicable To',
        accessorKey: 'applicableTo',
        size: 140,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.applicableTo),
      },
      {
        id: 'currency',
        header: 'Currency',
        accessorKey: 'defaultCurrencyCode',
        size: 110,
        enableSorting: false,
        cell: ({ row }) => row.original.defaultCurrencyCode || '—',
      },
      {
        id: 'rounding',
        header: 'Rounding',
        accessorKey: 'defaultRoundingFactorLabel',
        size: 130,
        enableSorting: false,
        cell: ({ row }) => row.original.defaultRoundingFactorLabel || '—',
      },
      {
        id: 'origin',
        header: 'Origin',
        accessorKey: 'isSystem',
        size: 100,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.isSystem ? (
            <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 999, background: '#dbeafe', color: '#1e3a8a', fontWeight: 600 }}>System</span>
          ) : (
            <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 999, background: '#dcfce7', color: '#166534', fontWeight: 600 }}>Firm</span>
          ),
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'statusName',
        size: 110,
        enableSorting: true,
        cell: ({ row }) => {
          const isActive = row.original.statusCode === 'ACTIVE';
          return (
            <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
              {row.original.statusName || (isActive ? 'Active' : 'Inactive')}
            </span>
          );
        },
      },
      {
        id: 'createdAt',
        header: 'Created',
        accessorKey: 'createdAt',
        size: 130,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.createdAt),
      },
      {
        id: 'updatedAt',
        header: 'Updated',
        accessorKey: 'updatedAt',
        size: 130,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.updatedAt),
      },
    ],
    []
  );

  const visibleTableColumns = useMemo<SyutGridColumn<FsTemplate>[]>(
    () => tableColumns.filter((col) => visibleColumns.includes(col.id as string)),
    [tableColumns, visibleColumns]
  );

  // Row actions — Stage 1 = view only
  const getRowActions = useCallback(
    (row: FsTemplate): ActionMenuItem[] => {
      const items: ActionMenuItem[] = [];
      if (allowedActions.includes('view')) {
        items.push({
          id: 'view',
          label: 'View',
          icon: <IconSystem.eye size={16} />,
          onClick: () => handleView(row),
        });
      }
      return items;
    },
    [allowedActions, handleView]
  );

  // Action buttons — Stage 1: refresh only
  const actionButtons: ActionButton[] = useMemo(
    () => [
      {
        id: 'refresh',
        icon: <IconSystem.refresh size={18} />,
        label: 'Refresh',
        tooltip: 'Refresh',
        onClick: () => refetch(),
        variant: 'icon' as const,
      },
    ],
    [refetch]
  );

  // Active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.code) count++;
    if (queryParams.name) count++;
    if (queryParams.entityType) count++;
    if (queryParams.jurisdictionCode) count++;
    if (queryParams.statutoryBasis) count++;
    if (queryParams.isSystem !== undefined) count++;
    if (queryParams.statusCode) count++;
    return count;
  }, [queryParams.code, queryParams.name, queryParams.entityType, queryParams.jurisdictionCode, queryParams.statutoryBasis, queryParams.isSystem, queryParams.statusCode]);

  // Apply / Clear
  const applyFilters = useCallback(() => {
    setQueryParams((prev) => ({
      ...prev,
      code: codeFilter || undefined,
      name: nameFilter || undefined,
      entityType: (entityTypeFilter || undefined) as EntityType | undefined,
      jurisdictionCode: jurisdictionFilter || undefined,
      statutoryBasis: statutoryBasisFilter || undefined,
      isSystem: originFilter === 'system' ? true : originFilter === 'firm' ? false : undefined,
      statusCode: statusFilter || undefined,
      includeInactive: true,
      page: 1,
    }));
    setFilterOpen(false);
  }, [codeFilter, nameFilter, entityTypeFilter, jurisdictionFilter, statutoryBasisFilter, originFilter, statusFilter]);

  const clearFilters = useCallback(() => {
    setCodeFilter('');
    setNameFilter('');
    setEntityTypeFilter('');
    setJurisdictionFilter('');
    setStatutoryBasisFilter('');
    setOriginFilter('');
    setStatusFilter('');
    setQueryParams((prev) => ({
      page: 1,
      pageSize: prev.pageSize,
      includeInactive: true,
      includeNarratives: true,
      searchTerm: searchQuery || undefined,
    }));
  }, [searchQuery]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="FSA Template Browser"
        description="Read-only browser of financial-statement templates and narrative components."
        icon={
          moduleIcon && (IconSystem as any)[moduleIcon]
            ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
            : <IconSystem.chart size={18} />
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search code, name, statutory basis…',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={data}
                columns={visibleTableColumns}
                enableRowSelection
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                enableSorting
                rowActions={allowedActions.includes('view') ? getRowActions : undefined}
                emptyMessage={loading ? 'Loading templates…' : 'No FSA templates found'}
              />
            )}

            {viewMode === 'grid' && (
              <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div
                  style={{
                    padding: '1rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                    gap: '1.5rem',
                    flex: 1,
                    overflow: 'auto',
                    alignContent: 'start',
                  }}
                >
                  {data.length === 0 && (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                      {loading ? 'Loading templates…' : 'No FSA templates found'}
                    </div>
                  )}
                  {data.map((item) => {
                    const isActive = item.statusCode === 'ACTIVE';
                    return (
                      <div
                        key={String(item.id)}
                        onClick={() => allowedActions.includes('view') && handleView(item)}
                        style={{
                          background: 'var(--bg-primary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: 8,
                          padding: '1rem',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5rem',
                          cursor: allowedActions.includes('view') ? 'pointer' : 'default',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '0.5rem' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                              {item.code}
                            </div>
                            <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0, marginTop: '0.25rem' }}>
                              {item.name}
                            </h3>
                          </div>
                          {item.kind === 'NARRATIVE' ? (
                            <span style={{ fontSize: '0.65rem', fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: '#ede9fe', color: '#5b21b6', whiteSpace: 'nowrap' }}>
                              Narrative
                            </span>
                          ) : item.isSystem && (
                            <span style={{ fontSize: '0.65rem', fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: '#dbeafe', color: '#1e3a8a', whiteSpace: 'nowrap' }}>
                              System
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                          {item.kind === 'NARRATIVE' ? (
                            <span><strong>Narrative component</strong> · v{item.version}</span>
                          ) : (
                            <>
                              <span><strong>Entity:</strong> {item.entityType}</span>
                              <span><strong>Jurisdiction:</strong> {item.jurisdictionCode}</span>
                              <span><strong>Basis:</strong> {item.statutoryBasis}</span>
                              {item.defaultCurrencyCode && (
                                <span><strong>Currency:</strong> {item.defaultCurrencyCode}</span>
                              )}
                              {item.defaultRoundingFactorLabel && (
                                <span><strong>Rounding:</strong> {item.defaultRoundingFactorLabel}</span>
                              )}
                            </>
                          )}
                        </div>
                        <div style={{ marginTop: 'auto' }}>
                          <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
                            {item.statusName || (isActive ? 'Active' : 'Inactive')}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Code</label>
                <input type="text" className="form-input" value={codeFilter}
                  onChange={(e) => setCodeFilter(e.target.value.toUpperCase())}
                  placeholder="Filter by code…"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Template Name</label>
                <input type="text" className="form-input" value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  placeholder="Filter by name…"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <SyutSelect label="Entity Type" options={ENTITY_TYPE_OPTIONS} value={entityTypeFilter}
                  onChange={(val) => setEntityTypeFilter((val as string) || '')} isClearable={false} size="sm" />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Jurisdiction</label>
                <input type="text" className="form-input" value={jurisdictionFilter}
                  onChange={(e) => setJurisdictionFilter(e.target.value.toUpperCase())}
                  placeholder="ISO-2 (e.g. IN)"
                  maxLength={2}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Statutory Basis</label>
                <input type="text" className="form-input" value={statutoryBasisFilter}
                  onChange={(e) => setStatutoryBasisFilter(e.target.value)}
                  placeholder="e.g. SCHEDULE_III_DIV_I"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <SyutSelect label="Origin" options={ORIGIN_OPTIONS} value={originFilter}
                  onChange={(val) => setOriginFilter((val as string) || '')} isClearable={false} size="sm" />
              </div>
              <div className="filter-section">
                <SyutSelect label="Status" options={STATUS_FILTER_OPTIONS} value={statusFilter}
                  onChange={(val) => setStatusFilter((val as string) || '')} isClearable={false} size="sm" />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )}
      </div>

      <FsaTemplateViewModal
        isOpen={viewModalOpen}
        template={viewRecord}
        onClose={() => {
          setViewModalOpen(false);
          setViewRecord(null);
        }}
      />

      <FsaNarrativeViewModal
        isOpen={narrativeView != null}
        narrativeCode={narrativeView?.code ?? null}
        version={narrativeView?.version}
        onClose={() => setNarrativeView(null)}
      />
    </div>
  );
};

export default FsaTemplateMasterScreen;
